import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Input, MobileInput, Typography } from '@pwskills/rachnaui';
import { toast } from 'react-toastify';
import { useRouter } from 'next/router';
import LeadCaptureApi from '@/api/leadCapture';
import { isEmail, isName, isValidIndianPhoneNumber } from '@/utils/common/common.validation';
import useUserDetails from '@/store/userDetails';
import { getPPClientDetails, setAccessToken, setCookies } from '@/utils/helper/helperFunction';
import { PP_TOKEN } from '@/utils/common/common.constants';
import { useCaptchaConfig } from '@/hooks/useCaptchaConfig';
import { sendSkOtpRequestInitiatedEvent } from '@/utils/GAEventFunction';
import { resolveCourseBatchContext } from '@/utils/GAEventFunction/helperUtils';
import { penPencilClientId } from '../../../utils/common/env.constants';
import { useCaptcha } from '../Captcha';
import CaptchaWidget from '../Captcha/CaptchaWidget';
import { CAPTCHA_WIDGET_IDS } from '../Captcha/constants';

interface UserVerificationFormProps {
  contactDetails: {
    fullName: string;
    phoneNumber: string;
    isdCode: string;
    email?: string;
  };
  setContactDetails: (data: any) => void;
}

const INITIAL_TIMER_COUNT = 30;
const SMS_TYPE = '1';
const USER_DOES_NOT_EXIST = 'User does not exist';

const UserVerificationForm = ({ contactDetails, setContactDetails }: UserVerificationFormProps) => {
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [isOtpVerified, setIsOtpVerified] = useState<boolean | null>(null);
  const [isValidPhoneNumber, setIsValidPhoneNumber] = useState(false);
  const [isError, setIsError] = useState({
    nameError: false,
    phoneNumberError: false,
    emailError: false,
    otpError: false,
  });
  const [otp, setOtp] = useState('');
  const [currentTimerCount, setCurrentTimerCount] = useState(0);
  const [isValidName, setIsValidName] = useState(false);
  const { setUserDetails, setProfileLoader, setUserIsLoggedIn } = useUserDetails();

  // Captcha-related state management
  const [captchaToken, setCaptchaToken] = useState<string>();
  const captcha = useCaptcha();
  const { isCaptchaEnabled, captchaConfig } = useCaptchaConfig();
  const otpAttemptCountRef = useRef(0);
  const otpResendCountRef = useRef(0);
  const widgetId = CAPTCHA_WIDGET_IDS.LEAD_FORM_MODAL2;
  const router = useRouter();

  // Helper function to reset captcha if enabled
  const resetCaptchaIfEnabled = () => {
    if (isCaptchaEnabled) {
      setCaptchaToken('');
      captcha?.reset(widgetId);
    }
  };

  const handleUserRegistration = async () => {
    const { phoneNumber } = contactDetails;
    const countryCode = contactDetails.isdCode;
    const firstName = contactDetails.fullName.split(' ')[0];
    await window?.PWWebSDK.registerUser({
      countryCode,
      phoneNumber,
      firstName,
      captchaPayload: {
        showCaptcha: isCaptchaEnabled,
        captchaToken,
        captchaSiteKey: captchaConfig?.siteKey,
      },
    });
  };

  const handleVerifyOTP = async (otpNum: string) => {
    const { phoneNumber } = contactDetails;
    const res = await window?.PWWebSDK?.verifyOTP(phoneNumber, otpNum);
    return res;
  };

  const handleGetOtp = async () => {
    try {
      otpAttemptCountRef.current += 1;
      sendSkOtpRequestInitiatedEvent({
        isLoggedIn: !!localStorage.getItem('TOKEN'),
        form_id: 'lead_form_modal2',
        form_name: 'Lead Form Modal 2',
        form_type: 'lead',
        form_location: typeof window !== 'undefined' ? window.location.pathname : undefined,
        cta_text: 'GET OTP',
        otp_channel: 'sms',
        request_type: 'first_request',
        otp_attempt_number: otpAttemptCountRef.current,
        resend_count: otpResendCountRef.current,
        user_phone_number: contactDetails.phoneNumber,
        user_phone_isd_code: contactDetails.isdCode,
        ...resolveCourseBatchContext(router?.query, router?.asPath),
        platform: 'web',
      });

      setIsOtpVerified(null);
      setOtp('');
      setIsOtpSent(false);
      setCurrentTimerCount(INITIAL_TIMER_COUNT);
      const response = await window.PWWebSDK?.getOTP(
        contactDetails.phoneNumber,
        contactDetails.isdCode,
        contactDetails.isdCode === '+91' ? 0 : 1,
        false,
        false,
        {
          showCaptcha: isCaptchaEnabled,
          captchaToken,
          captchaSiteKey: captchaConfig?.siteKey,
        }
      );
      if (response?.message === USER_DOES_NOT_EXIST) {
        setIsOtpSent(true);
        setOtp('');
        handleUserRegistration();
        resetCaptchaIfEnabled();
      }

      if (response?.success) {
        setIsOtpSent(true);
        setOtp('');
        resetCaptchaIfEnabled();
      }
    } catch (error: any) {
      /* eslint-disable no-console */
      console.log(error);
      if (error?.error?.status === 400) {
        if (error?.error?.message === USER_DOES_NOT_EXIST) {
          setIsOtpSent(true);
          setOtp('');
          handleUserRegistration();
        }
        setIsOtpSent(true);
        setOtp('');
        resetCaptchaIfEnabled();
      }
    }
  };

  const handleResendOtp = async () => {
    otpResendCountRef.current += 1;
    otpAttemptCountRef.current += 1;
    sendSkOtpRequestInitiatedEvent({
      isLoggedIn: !!localStorage.getItem('TOKEN'),
      form_id: 'lead_form_modal2',
      form_name: 'Lead Form Modal 2',
      form_type: 'lead',
      form_location: typeof window !== 'undefined' ? window.location.pathname : undefined,
      cta_text: 'Resend OTP',
      otp_channel: 'sms',
      request_type: 'resend',
      otp_attempt_number: otpAttemptCountRef.current,
      resend_count: otpResendCountRef.current,
      user_phone_number: contactDetails.phoneNumber,
      user_phone_isd_code: contactDetails.isdCode,
      ...resolveCourseBatchContext(router?.query, router?.asPath),
      platform: 'web',
    });

    setIsOtpVerified(null);
    const dataToBeSend = {
      mobile: contactDetails.phoneNumber,
      organizationId: getPPClientDetails()?.clientId || penPencilClientId,
      captchaToken,
      captchaSiteKey: captchaConfig?.siteKey,
    };
    try {
      const smsType = contactDetails.isdCode !== '+91' ? SMS_TYPE : '0';
      setCurrentTimerCount(INITIAL_TIMER_COUNT);
      const response = await LeadCaptureApi.resendOtp(dataToBeSend, smsType, isCaptchaEnabled);
      if (response) {
        setIsOtpSent(true);
        setOtp('');
        resetCaptchaIfEnabled();
      }
    } catch (error: any) {
      if (error?.response?.status === 400) {
        setIsOtpSent(true);
        setOtp('');
        resetCaptchaIfEnabled();
      }
    }
  };

  const isResendDisabled = useMemo(() => {
    return currentTimerCount > 0;
  }, [currentTimerCount]);

  const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    // Allow only digits
    if (/^\d*$/.test(input)) {
      // Allow only up to 4 digits
      if (input.length <= 6) {
        setOtp(input);
      }
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    if (value.length > 0 && value.trim() !== '') {
      setIsValidName(true);
    } else {
      setIsValidName(false);
    }

    setContactDetails((prevState: any) => ({ ...prevState, [name]: value }));
  };

  const updateUserDetails = () => {
    const userRaw = localStorage.getItem('user');
    const accessToken = localStorage.getItem('TOKEN') || '';
    if (!userRaw) {
      /* eslint-disable no-console */
      console.error('No user data in localStorage');
      return;
    }
    const parsedUser = JSON.parse(userRaw);
    setUserDetails(parsedUser);
    setAccessToken(accessToken);
    setCookies(PP_TOKEN, accessToken);
    setCookies('ppToken', accessToken);
    setUserIsLoggedIn(true);
    setProfileLoader(false);
  };

  const handleSubmit = async () => {
    if (
      !isName(contactDetails.fullName) &&
      !isEmail((contactDetails.email && contactDetails?.email) || '') &&
      !isValidIndianPhoneNumber(contactDetails.phoneNumber, contactDetails.isdCode)
    ) {
      try {
        const response = await handleVerifyOTP(otp);
        if (response) {
          setIsOtpVerified(true);
          setIsOtpSent(false);
          updateUserDetails();
        }
      } catch (error: any) {
        setIsError({ ...isError, otpError: true });
        setIsOtpVerified(false);
      }
    } else {
      setIsError({
        ...isError,
        nameError: !!isName(contactDetails.fullName),
        emailError: !!isEmail((contactDetails.email && contactDetails?.email) || ''),
        phoneNumberError: !!isValidIndianPhoneNumber(
          contactDetails.phoneNumber,
          contactDetails.isdCode
        ),
      });
      setIsOtpVerified(false);
      setIsOtpSent(false);
    }
  };

  const renderOtpField = () => {
    return (
      <div className="flex flex-col gap-1">
        <div className="w-full inline-flex items-end border-b border-b-[#B7B7B7]">
          <Input
            id="otp"
            type="text"
            value={otp}
            disabled={!isValidPhoneNumber || !isOtpSent}
            onChange={(e) => {
              handleOtpChange(e);
            }}
            className="!w-full border-none"
            wrapperClass="flex !flex-grow !w-full [&>div]:!w-full"
            label="OTP"
          />

          {isOtpSent &&
            (isResendDisabled ? (
              <Typography
                variant="bold"
                component="body-tiny"
                className="text-orange-500 w-fit font-semibold whitespace-nowrap"
              >
                00:
                {currentTimerCount.toLocaleString('en-IN', {
                  minimumIntegerDigits: 2,
                })}
              </Typography>
            ) : (
              <button
                type="button"
                disabled={!isValidPhoneNumber && isOtpSent}
                onClick={() => {
                  if (isCaptchaEnabled && !captchaToken) {
                    toast.error('Please complete the captcha verification');
                    return;
                  }
                  handleResendOtp();
                }}
                className={`w-fit font-semibold whitespace-nowrap ${
                  currentTimerCount !== 0 && (!isValidPhoneNumber || isOtpSent)
                    ? '!text-orange-100'
                    : '!text-orange-500'
                } p-1 text-xs medium:text-base`}
              >
                Resend OTP
              </button>
            ))}
        </div>
      </div>
    );
  };

  const renderInput = () => {
    return (
      <div className="flex items-end !w-full border-b border-b-[#B7B7B7]">
        <Input
          id="name"
          wrapperClass="!w-full"
          className="!w-full border-none"
          label="Name"
          name="fullName"
          value={contactDetails.fullName}
          onChange={(nameData) => {
            handleChange(nameData);
            setIsError({ ...isError, nameError: false });
          }}
          error={isError.nameError}
          helperText={(isError.nameError && isName(contactDetails.fullName)) || ''}
          isRequired
        />
      </div>
    );
  };

  const renderPhoneNumber = () => {
    return (
      <div className="flex items-end !w-full">
        <div className="!w-full" id="phoneNumber">
          <MobileInput
            // id="get_in_touch_phoneNumber"
            autoFocus={false}
            defaultCountry="IN"
            wrapperClass="mt-5 flex !flex-grow !w-full [&>div]:border-none"
            className="w-full medium:min-w-[264px] large:min-w-[366px] border-none "
            countrySearch
            error={isError.phoneNumberError}
            helperText={
              (isError.phoneNumberError &&
                isValidIndianPhoneNumber(contactDetails.phoneNumber, contactDetails.isdCode)) ||
              ''
            }
            onChange={(e) => {
              setContactDetails({ ...contactDetails, phoneNumber: e });
              setIsError({ ...isError, phoneNumberError: false });
            }}
            isValid={(val) => {
              setIsValidPhoneNumber(val);
              if (val && isOtpSent) {
                setIsOtpSent(false);
                setIsOtpVerified(false);
              }
            }}
            value={contactDetails.phoneNumber}
            countryCode={(val) => setContactDetails({ ...contactDetails, isdCode: val })}
          />
        </div>
      </div>
    );
  };

  const renderOtpButton = () => {
    const handleOtpClick = () => {
      if (isCaptchaEnabled && !captchaToken) {
        toast.error('Please complete the captcha verification');
        return;
      }
      handleGetOtp();
    };

    const isDisabled = !isValidPhoneNumber || isOtpSent || !isValidName;

    return (
      <button
        type="button"
        disabled={isDisabled}
        onClick={handleOtpClick}
        className={`text-xs medium:text-base font-semibold whitespace-nowrap ${
          isDisabled ? '!text-orange-100' : '!text-orange-500'
        } p-1 `}
      >
        {isOtpSent ? 'OTP Sent' : 'GET OTP'}
      </button>
    );
  };

  const isSubmitDisabled = useMemo(() => {
    const formDisabled = !isValidName || !isOtpSent || otp.length !== 6;
    const captchaDisabled = isCaptchaEnabled && !captchaToken;
    return formDisabled || captchaDisabled;
  }, [isValidName, isOtpSent, otp, isCaptchaEnabled, captchaToken]);

  const renderSubmitButton = () => {
    return (
      <Button type="submit" className="w-40" disabled={isSubmitDisabled}>
        Submit
      </Button>
    );
  };

  const renderError = () => {
    if (isOtpSent && isOtpVerified === false && isError.otpError) {
      return (
        <Typography variant="semi-bold" component="body-tiny" className="!text-red-500">
          *Invalid OTP
        </Typography>
      );
    }
    return null;
  };

  useEffect(() => {
    let timer: ReturnType<typeof setInterval>;
    if (contactDetails.phoneNumber) {
      if (currentTimerCount > 0) {
        timer = setInterval(() => {
          setCurrentTimerCount((prev: number) => prev - 1);
        }, 1000);
      }
    }
    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [currentTimerCount]);

  const renderCaptcha = () => {
    if (isCaptchaEnabled) {
      return (
        <div className="mt-4">
          <CaptchaWidget onVerify={setCaptchaToken} widgetId={widgetId} />
        </div>
      );
    }
    return null;
  };

  return (
    <form
      id="form_input_field"
      className="w-full flex flex-col gap-6 justify-between items-center"
      onSubmit={(event) => {
        event.preventDefault();
        handleSubmit();
      }}
    >
      <div className="max-w-[264px] medium:max-w-[602px]">
        {renderInput()}
        <div className="w-full inline-flex items-end border-b border-b-[#B7B7B7]">
          {renderPhoneNumber()}
          {renderOtpButton()}
        </div>
        {renderOtpField()}
        {renderError()}
        {renderCaptcha()}
      </div>
      {renderSubmitButton()}
    </form>
  );
};

export default UserVerificationForm;
